import { useState, useEffect } from 'react';
import './AmuraModel.css';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../../auth';
import { AmuraForm, AmuraFormValues } from './AmuraForm';
import { AmuraList, AmuraData } from './AmuraList';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';

type Props = {
  show: boolean;
  handleClose: () => void;
};

const WEBHOOK_URL = 'https://etl.futurisk.in/webhook/79465ee9-de49-404d-a7d2-f5d1a58e445e';
const LOAD_REQUESTS_URL = 'https://etl.futurisk.in/webhook/30143c1c-e085-4d0d-94ef-8564b80286e2';

const AmuraModel: React.FC<Props> = ({ show, handleClose }) => {
  const auth = useAuth();
  const [requests, setRequests] = useState<AmuraData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [redirectingUrl, setRedirectingUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Load requests when modal opens
  useEffect(() => {
    let mounted = true;
    const loadRequests = async () => {
      if (!show) {
        // Reset state when modal closes
        setRequests([]);
        setShowForm(false);
        setIsSubmitted(false);
        setSubmissionError(null);
        setRedirectingUrl(null);
        return;
      }
      
      setLoading(true);
      setShowForm(false);
      setIsSubmitted(false);
      setSubmissionError(null);
      setRedirectingUrl(null);
      setRequests([]);
      
      try {
        const payload = {
          CRMClientId: auth.currentUser?.CRMClientId,
          EmployeeId: auth.currentUser?.EmployeeId,
        };

        const resp = await fetch(LOAD_REQUESTS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          if (mounted) {
            setRequests([]);
            setShowForm(true);
          }
        } else {
          const data = await resp.json();
          let requestsData: AmuraData[] = [];

          if (Array.isArray(data)) {
            requestsData = data;
          } else if (data?.body) {
            requestsData = Array.isArray(data.body) ? data.body : [data.body];
          } else {
            requestsData = [data];
          }

          if (mounted) {
            if (requestsData.length === 0) {
              setShowForm(true);
            }
            setRequests(requestsData);
          }
        }
      } catch (err) {
        if (mounted) {
          // On error, show form
          setRequests([]);
          setShowForm(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRequests();
    return () => { mounted = false; };
  }, [show, auth.currentUser]);

  const handleFormSubmit = async (values: AmuraFormValues) => {
    setSubmissionError(null);
    setCreating(true);
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNumber: `+91${values.mobileNumber}`,
        email: values.email,
        healthObjectives: values.healthObjectives || '',
        age: values.age || '',
        gender: values.gender || '',
        weight: values.weight || '',
        height: values.height || '',
        CRMClientId: auth.currentUser?.CRMClientId,
        EmployeeId: auth.currentUser?.EmployeeId,
      };

      const resp = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Server error: ${resp.status} ${text}`);
      }

      const data = await resp.json();

      let redirectUrl: string | undefined;
      try {
        if (Array.isArray(data) && data.length > 0 && data[0].body?.responseData?.url) {
          redirectUrl = data[0].body.responseData.url;
        } else if (data?.body?.responseData?.url) {
          redirectUrl = data.body.responseData.url;
        } else if (data?.responseData?.url) {
          redirectUrl = data.responseData.url;
        }
      } catch (e) {
        // ignore parsing errors
      }

      setIsSubmitted(true);

      if (redirectUrl) {
        setRedirectingUrl(redirectUrl);
        try { window.open(redirectUrl, '_blank'); } catch (e) {}
        setTimeout(() => handleClose(), 800);
      } else {
        // On success, reload requests after a short delay
        setTimeout(async () => {
          try {
            const payload = {
              CRMClientId: auth.currentUser?.CRMClientId,
              EmployeeId: auth.currentUser?.EmployeeId,
            };

            const resp = await fetch(LOAD_REQUESTS_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            if (resp.ok) {
              const data = await resp.json();
              let requestsData: AmuraData[] = [];

              if (Array.isArray(data)) {
                requestsData = data;
              } else if (data?.body) {
                requestsData = Array.isArray(data.body) ? data.body : [data.body];
              } else {
                requestsData = [data];
              }

              setRequests(requestsData);
              setShowForm(false);
            }
          } catch (err) {
            // ignore
          } finally {
            setLoading(false);
          }
        }, 1500);
      }
    } catch (err: any) {
      setSubmissionError(err?.message || 'Submission failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_amura'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      aria-hidden='true'
      dialogClassName=' modal-lg'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Get Started with Amura Health</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>

        </div>
        <div className='modal-body'>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading requests...</div>
            </div>
          ) : redirectingUrl ? (
            <div className="text-center">
              <div className="mb-3">Redirecting you...</div>
              <div>If nothing happens, <a href={redirectingUrl} target="_blank" rel="noopener noreferrer">click here</a>.</div>
            </div>
          ) : isSubmitted ? (
            <div className="alert alert-success" role="alert">
              The Amura Health team has received your details and will get in touch shortly. You may learn more about Amura Health here: <a href="https://amura.ai" target="_blank" rel="noopener noreferrer">https://amura.ai</a>
            </div>
          ) : (showForm || requests.length === 0) ? (
            <AmuraForm 
                submissionError={submissionError}
                creating={creating}
                onSubmit={handleFormSubmit}
                initialFirstName={auth.currentUser?.Name?.split(' ')[0] || ''}
                initialLastName={auth.currentUser?.Name?.split(' ').slice(1).join(' ') || ''}
                initialEmail={auth.currentUser?.Email! || ''}
                initialPhone={auth.currentUser?.PhoneNumber! || ''}
              />
          ) : (
            <AmuraList 
                requests={requests}
                onAddRequest={() => setShowForm(true)}
              />
          )}
        </div>
        <div className='modal-footer'>
          * terms and conditions apply
        </div>
      </div>
    </Modal>
  );
};

export { AmuraModel };
